/**
 * Dance / Emote sprite animations.
 * Maps each DANCE_EMOTES.id to a transform applied around the sprite draw call.
 *
 * Returns:
 *   {
 *     direction: 'up'|'down'|'left'|'right',
 *     walkPhase: number (0-8 column index, 0 = idle, 1-8 = walk),
 *     isWalking: bool (whether to use walking columns),
 *     yOffset: number (vertical pixel offset),
 *     xOffset: number (horizontal pixel offset),
 *     rotation: number (radians around sprite center),
 *     scaleX: number (horizontal scale, default 1),
 *     scaleY: number (vertical scale, default 1),
 *   }
 */

const DEFAULT_TRANSFORM = {
  direction: 'down',
  walkPhase: 0,
  isWalking: false,
  yOffset: 0,
  xOffset: 0,
  rotation: 0,
  scaleX: 1,
  scaleY: 1,
};

// Helper — pick row by time
const pickDirection = (t, msPerStep, sequence) => {
  const i = Math.floor(t / msPerStep) % sequence.length;
  return sequence[i];
};

// Fake-3D spin width: cos wave narrows the sprite like it's turning
const spinSX = (t, speed) => Math.max(0.22, Math.abs(Math.cos(t * speed)));

// Eased beat 0→1→0 with a snappy attack and soft release
const snapBeat = (t, period) => {
  const p = (t % period) / period;
  return p < 0.3 ? Math.sin((p / 0.3) * Math.PI * 0.5) : Math.cos(((p - 0.3) / 0.7) * Math.PI * 0.5);
};

// Turtle Man choreography — every emote gets its own shell-powered animation.
// Same output shape as getDanceTransform; walkPhase feeds the special sheet columns.
export function getTurtleDance(emoteId, t) {
  const base = { direction: 'down', isWalking: false, walkPhase: 0, xOffset: 0, yOffset: 0, rotation: 0, scaleX: 1, scaleY: 1 };
  switch (emoteId) {
    case 'wave': // happy lean-back rock with little bounces
      return { ...base, rotation: Math.sin(t * 0.014) * 0.22, yOffset: -Math.abs(Math.sin(t * 0.007)) * 4 };
    case 'dance1': { // shell bounce head-bob
      const b = Math.abs(Math.sin(t * 0.012));
      return { ...base, yOffset: -b * 6, scaleY: 1 - b * 0.12, scaleX: 1 + b * 0.1, isWalking: true, walkPhase: t / 200 };
    }
    case 'dance2': { // scooting side-to-side shuffle
      const s = Math.sin(t * 0.006);
      return { ...base, xOffset: s * 16, rotation: s * 0.12, direction: s > 0 ? 'right' : 'left', isWalking: true, walkPhase: t / 130 };
    }
    case 'dance3': { // two tiny turtle hops... then one HEROIC leap
      const c = t % 2400;
      let hop = 0;
      if (c < 500) hop = Math.abs(Math.sin((c / 500) * Math.PI)) * 4;
      else if (c < 1000) hop = Math.abs(Math.sin(((c - 500) / 500) * Math.PI)) * 4;
      else if (c < 1300) hop = 0; // crouch wind-up
      else if (c < 2100) hop = Math.sin(((c - 1300) / 800) * Math.PI) * 34;
      const squash = c >= 1000 && c < 1300 ? 0.78 : 1;
      return { ...base, yOffset: -hop, scaleY: squash, scaleX: squash < 1 ? 1.2 : 1 };
    }
    case 'spin': { // tucks into his shell, spins like a top... then dizzy wobble
      const tuck = Math.min(1, t / 300);
      if (t < 2200) {
        return { ...base, rotation: t * 0.02 * tuck, scaleX: 1 - tuck * 0.18, scaleY: 1 - tuck * 0.26, yOffset: tuck * 6 };
      }
      const w2 = t - 2200; // 2200*0.02 ≈ 7 full turns — lands ~upright
      return { ...base, rotation: Math.sin(w2 * 0.02) * 0.3 * Math.exp(-w2 / 500), xOffset: Math.sin(w2 * 0.03) * 3 };
    }
    case 'headbang': {
      const n = Math.sin(t * 0.02);
      return { ...base, rotation: n * 0.5, yOffset: -Math.abs(n) * 3 };
    }
    case 'dab': { // snappy shell dab
      const p = Math.min(1, t / 250);
      return { ...base, rotation: -0.42 * p, xOffset: -6 * p, yOffset: -4 * p, direction: 'left' };
    }
    case 'clap': { // flipper claps = full-body squish
      const c = Math.abs(Math.sin(t * 0.015));
      return { ...base, scaleX: 1 + c * 0.12, scaleY: 1 - c * 0.06, yOffset: -c * 3 };
    }
    case 'point':
      return { ...base, rotation: -0.2 + Math.sin(t * 0.008) * 0.05, yOffset: -2, direction: 'up' };
    case 'heart': { // double heartbeat thump
      const c = t % 1000;
      const pulse = c < 120 ? 1.16 : c < 220 ? 1.04 : c < 340 ? 1.12 : 1;
      return { ...base, scaleX: pulse, scaleY: pulse, yOffset: -(pulse - 1) * 14 };
    }
    case 'robot': { // turtle.exe — stiff quantized poses
      const stp = Math.floor(t / 260) % 4;
      return { ...base, rotation: [0.14, 0, -0.14, 0][stp], xOffset: [6, 0, -6, 0][stp], direction: ['right', 'down', 'left', 'down'][stp], scaleY: 0.98 };
    }
    case 'moonwalk': { // impossibly smooth backwards glide
      const s = Math.sin(t * 0.0035);
      return { ...base, xOffset: s * 30, direction: s > 0 ? 'left' : 'right', isWalking: true, walkPhase: t / 90, rotation: -s * 0.06 };
    }
    case 'breakdance': { // flips onto his shell and SPINS on it
      const flip = Math.min(1, t / 400);
      return { ...base, rotation: flip * Math.PI + (t > 400 ? (t - 400) * 0.014 : 0), yOffset: 8 * flip, scaleY: 1 - flip * 0.1 };
    }
    case 'floss': {
      const s = Math.sin(t * 0.022);
      return { ...base, rotation: s * 0.24, xOffset: -s * 9, scaleY: 1 + Math.abs(s) * 0.04 };
    }
    case 'running_man': // sprinting... at turtle speed, going nowhere
      return { ...base, isWalking: true, walkPhase: t / 70, yOffset: -Math.abs(Math.sin(t * 0.02)) * 5 };
    case 'disco': { // Saturday Night Shell — alternating point poses
      const stp = Math.floor(t / 420) % 2;
      return { ...base, rotation: stp ? 0.18 : -0.18, yOffset: stp ? -6 : 0, scaleY: 1.04 };
    }
    case 'salsa': {
      const s = Math.sin(t * 0.007);
      return { ...base, xOffset: s * 14, yOffset: -Math.abs(Math.cos(t * 0.007)) * 4, rotation: s * 0.14, direction: s > 0 ? 'right' : 'left', isWalking: true, walkPhase: t / 160 };
    }
    case 'pop_lock': { // freeze-frame poses
      const stp = Math.floor(t / 300) % 5;
      return { ...base, rotation: [0, 0.22, 0.22, -0.18, 0][stp], xOffset: [0, 8, 8, -8, 0][stp], yOffset: [0, -4, -4, 0, 0][stp], scaleY: [1, 0.94, 0.94, 1.05, 1][stp] };
    }
    case 'worm': { // belly-slide shell ripple
      const w = Math.sin(t * 0.012);
      return { ...base, scaleY: 0.62 + Math.abs(w) * 0.16, scaleX: 1.25, yOffset: 10 - Math.abs(w) * 6, xOffset: Math.sin(t * 0.004) * 12, rotation: w * 0.08 };
    }
    case 'chicken': { // pecking lunges with stubby wing flaps
      const pk = Math.abs(Math.sin(t * 0.016));
      const stp2 = Math.floor(t / 500) % 2;
      return { ...base, rotation: pk * 0.3, yOffset: -pk * 2, xOffset: stp2 ? 6 : -6, direction: stp2 ? 'right' : 'left', scaleX: 1 + pk * 0.06, isWalking: true, walkPhase: t / 150 };
    }
    case 'twerk': { // shell to the crowd, rapid bounce
      const b2 = Math.abs(Math.sin(t * 0.03));
      return { ...base, direction: 'up', scaleY: 0.9 - b2 * 0.08, scaleX: 1.08, yOffset: 6 + b2 * 4, rotation: Math.sin(t * 0.03) * 0.05 };
    }
    case 'macarena': { // pose sequence, then the hip-shake hop
      const seq = Math.floor(t / 400) % 8;
      if (seq === 7) return { ...base, yOffset: -Math.abs(Math.sin(t * 0.02)) * 10, rotation: Math.sin(t * 0.02) * 0.15 };
      const rots = [0.12, -0.12, 0.2, -0.2, 0.08, -0.08, 0.15];
      const dirs = ['down', 'down', 'left', 'right', 'down', 'down', 'down'];
      return { ...base, rotation: rots[seq], xOffset: rots[seq] * 40, direction: dirs[seq], scaleY: 1.02 };
    }
    default: { // safe happy bounce — never an unintended spin
      const b3 = Math.abs(Math.sin(t * 0.01));
      return { ...base, yOffset: -b3 * 8, scaleY: 1 + b3 * 0.05, isWalking: true, walkPhase: t / 160 };
    }
  }
}

export function getDanceTransform(emoteId, elapsed) {
  if (!emoteId) return DEFAULT_TRANSFORM;
  const t = elapsed;

  switch (emoteId) {
    // --- Gestures ---
    case 'wave': {
      // Friendly side-to-side lean with small bounces, facing you
      const lean = Math.sin(t * 0.012) * 0.16;
      const hop = Math.abs(Math.sin(t * 0.008)) * 3;
      return { ...DEFAULT_TRANSFORM, rotation: lean, yOffset: -hop, scaleY: 1 + Math.abs(lean) * 0.12 };
    }
    case 'heart': {
      // Double heartbeat thump — thu-thump ... thu-thump
      const c = t % 1000;
      const pulse = c < 120 ? 1.16 : c < 220 ? 1.04 : c < 340 ? 1.12 : 1 + Math.sin((c / 1000) * Math.PI) * 0.02;
      return { ...DEFAULT_TRANSFORM, scaleX: pulse, scaleY: pulse, yOffset: -(pulse - 1) * 16 };
    }
    case 'point': {
      // Look up + rise on tip-toes on each beat
      const rise = snapBeat(t, 700) * 5;
      return { ...DEFAULT_TRANSFORM, direction: 'up', yOffset: -rise, scaleY: 1 + rise * 0.012, rotation: Math.sin(t * 0.004) * 0.05 };
    }
    case 'clap': {
      // Rhythmic hop-claps with landing squash
      const beat = (t % 480) / 480;
      const hop = Math.sin(beat * Math.PI) * 7;
      const land = beat < 0.18;
      return { ...DEFAULT_TRANSFORM, yOffset: -hop, scaleX: land ? 1.12 : 0.98, scaleY: land ? 0.9 : 1.04, rotation: Math.sin(t * 0.013) * 0.05 };
    }
    case 'dab': {
      // Sharp lunge into the dab, hold with a bounce, small rebound at the end
      const p = Math.min(1, t / 220);
      const ease = 1 - Math.pow(1 - p, 3);
      const bounce = p >= 1 ? Math.sin(t * 0.014) * 2 : 0;
      return { ...DEFAULT_TRANSFORM, direction: 'right', rotation: -0.38 * ease, xOffset: 7 * ease, yOffset: 4 * ease + bounce, walkPhase: 3, isWalking: true, scaleX: 1 + 0.06 * ease };
    }

    // --- Bounce dances (squash & stretch) ---
    case 'dance1': {
      // Head-bob groove: bounce on the beat, alternate slight looks left/right
      const beat = (t % 560) / 560;
      const bounce = Math.sin(beat * Math.PI) * 8;
      const land = beat < 0.14 || beat > 0.86;
      const dir = pickDirection(t, 1120, ['down', 'down', 'left', 'down', 'down', 'right']);
      return { ...DEFAULT_TRANSFORM, direction: dir, yOffset: -bounce, scaleY: land ? 0.88 : 1.07, scaleX: land ? 1.12 : 0.96, rotation: Math.sin(t * 0.0056) * 0.1 };
    }
    case 'dance3': {
      // Big star jumps, alternating facing, hard squash on landing
      const beat = (t % 750) / 750;
      const jump = Math.sin(beat * Math.PI) * 26;
      const land = beat < 0.12 || beat > 0.88;
      const dir = Math.floor(t / 750) % 2 ? 'left' : 'right';
      return { ...DEFAULT_TRANSFORM, direction: dir, yOffset: -jump, scaleY: land ? 0.78 : 1.16, scaleX: land ? 1.2 : 0.9, isWalking: true, walkPhase: 1 + beat * 6, rotation: (dir === 'left' ? -1 : 1) * Math.sin(beat * Math.PI) * 0.08 };
    }
    case 'worm': {
      // Low ground ripple: heavy squash wave traveling through the body
      const w = t * 0.011;
      return { ...DEFAULT_TRANSFORM, yOffset: Math.sin(w) * 9 + 10, rotation: Math.cos(w) * 0.35, scaleY: 0.72 + Math.abs(Math.cos(w)) * 0.3, scaleX: 1.24, xOffset: Math.sin(w * 0.5) * 6 };
    }
    case 'twerk': {
      // Back to camera, deep low bounce with hip shake
      const b = Math.abs(Math.sin(t * 0.026));
      const shake = Math.sin(t * 0.052) * 3;
      return { ...DEFAULT_TRANSFORM, direction: 'up', scaleY: 0.94 + b * 0.16, scaleX: 1.06, yOffset: 6 - b * 4, xOffset: shake, rotation: Math.sin(t * 0.026) * 0.06 };
    }

    // --- Footwork ---
    case 'dance2': {
      // Shuffle: quick side steps with kick frames and a slide
      const dir = pickDirection(t, 220, ['left', 'left', 'right', 'right']);
      const hop = Math.abs(Math.sin(t * 0.0143)) * 5;
      const slide = Math.sin(t * 0.0071) * 10;
      return { ...DEFAULT_TRANSFORM, direction: dir, walkPhase: 1 + (t * 0.018) % 7, isWalking: true, yOffset: -hop, xOffset: slide, rotation: slide * 0.008 };
    }
    case 'running_man': {
      // Stationary run: fast leg cycle, pumping bounce, slight forward lean
      const hop = Math.abs(Math.sin(t * 0.024)) * 6;
      return { ...DEFAULT_TRANSFORM, walkPhase: 1 + (t * 0.032) % 7, isWalking: true, yOffset: -hop, rotation: 0.06 + Math.sin(t * 0.024) * 0.04, xOffset: Math.sin(t * 0.012) * 2, scaleY: 1 + hop * 0.008 };
    }
    case 'chicken': {
      // Head-pecking strut, flapping side to side
      const dir = pickDirection(t, 160, ['left', 'right']);
      const peck = Math.abs(Math.sin(t * 0.02)) * 5;
      return { ...DEFAULT_TRANSFORM, direction: dir, walkPhase: 1 + (t * 0.02) % 7, isWalking: true, rotation: Math.sin(t * 0.02) * 0.2, yOffset: peck - 2, scaleX: 1.08, scaleY: 0.97 };
    }
    case 'salsa': {
      // Smooth hip sway with cross-steps and rhythm pauses
      const dir = pickDirection(t, 260, ['left', 'down', 'right', 'down']);
      const sway = Math.sin(t * 0.006);
      return { ...DEFAULT_TRANSFORM, direction: dir, walkPhase: 1 + (t * 0.012) % 7, isWalking: true, xOffset: sway * 10, rotation: sway * 0.14, yOffset: -Math.abs(Math.sin(t * 0.012)) * 3, scaleY: 1 + Math.abs(sway) * 0.03 };
    }
    case 'floss': {
      // Arm-swing implied by sharp counter-rotation hip snaps
      const swing = Math.sin(t * 0.021);
      const snap = Math.sign(swing) * Math.pow(Math.abs(swing), 0.6);
      return { ...DEFAULT_TRANSFORM, direction: snap > 0 ? 'right' : 'left', walkPhase: 4, isWalking: true, xOffset: snap * 7, rotation: -snap * 0.18, yOffset: -Math.abs(snap) * 3, scaleX: 1 + Math.abs(snap) * 0.05 };
    }
    case 'macarena': {
      // Four-count routine: face front, right, front, left — hip circle on count 4
      const step = Math.floor(t / 380) % 4;
      const dirs = ['down', 'right', 'down', 'left'];
      const p = (t % 380) / 380;
      const hop = step === 3 ? Math.sin(p * Math.PI) * 8 : 0;
      const wiggle = step === 3 ? Math.sin(t * 0.03) * 4 : 0;
      return { ...DEFAULT_TRANSFORM, direction: dirs[step], walkPhase: 1 + (t * 0.01) % 4, isWalking: true, rotation: Math.sin(t * 0.008) * 0.12, yOffset: -hop, xOffset: wiggle };
    }

    // --- Spins (fake 3D via width) ---
    case 'spin': {
      // Smooth pirouette: sequence through all 4 rows while width squeezes
      const dir = pickDirection(t, 130, ['down', 'left', 'up', 'right']);
      return { ...DEFAULT_TRANSFORM, direction: dir, scaleX: spinSX(t, 0.0121), walkPhase: 1, isWalking: true, yOffset: -Math.abs(Math.sin(t * 0.006)) * 4, scaleY: 1.02 };
    }
    case 'breakdance': {
      // Alternate: 900ms ground spin → 500ms freeze pose, repeat
      const cycle = t % 1400;
      if (cycle < 900) {
        const rot = (cycle / 900) * Math.PI * 2 * 1.5;
        return { ...DEFAULT_TRANSFORM, direction: pickDirection(cycle, 150, ['down', 'left', 'up', 'right']), rotation: rot, yOffset: 14, scaleY: 0.85, scaleX: 1.1, walkPhase: 1 + (cycle * 0.024) % 7, isWalking: true };
      }
      const f = Math.floor(t / 1400) % 2;
      return { ...DEFAULT_TRANSFORM, direction: f ? 'left' : 'right', rotation: f ? -0.5 : 0.5, yOffset: 8, walkPhase: 5, isWalking: true, scaleX: 1.08, scaleY: 0.92 };
    }
    case 'disco': {
      // Saturday-night point: alternate up-point poses with hip drops
      const step = Math.floor(t / 420) % 4;
      const p = (t % 420) / 420;
      const ease = 1 - Math.pow(1 - Math.min(1, p * 2.2), 2);
      const dirs = ['right', 'up', 'left', 'up'];
      const rots = [0.22, -0.1, -0.22, 0.1];
      return { ...DEFAULT_TRANSFORM, direction: dirs[step], rotation: rots[step] * ease, walkPhase: step % 2 ? 2 : 3, isWalking: true, yOffset: -Math.sin(p * Math.PI) * 6, xOffset: (step === 0 ? 5 : step === 2 ? -5 : 0) * ease };
    }

    // --- Robot / stop-motion (hard quantized) ---
    case 'robot': {
      // Mechanical ticks: quantized poses, zero easing, tiny servo jitter
      const i = Math.floor(t / 240);
      const dirs = ['down', 'right', 'right', 'up', 'left', 'left'];
      const tilt = [0, 0.14, -0.08, 0, -0.14, 0.08][i % 6];
      const jitter = (i % 2) * 1.5;
      return { ...DEFAULT_TRANSFORM, direction: dirs[i % 6], walkPhase: i % 3 === 1 ? 2 : i % 3 === 2 ? 5 : 0, isWalking: i % 3 !== 0, rotation: tilt, yOffset: -jitter, xOffset: (i % 4 < 2 ? 2 : -2), scaleX: 1 + (i % 2) * 0.04 };
    }
    case 'pop_lock': {
      // Hit → hold → travel: hard locks with sharp scale hits
      const i = Math.floor(t / 170);
      const lock = i % 3 === 2;
      return { ...DEFAULT_TRANSFORM, direction: i % 4 < 2 ? 'right' : 'left', walkPhase: lock ? 6 : 1 + (i % 5), isWalking: true, rotation: lock ? (i % 2 ? 0.22 : -0.22) : 0, scaleX: lock ? 1.14 : 0.98, scaleY: lock ? 0.92 : 1.03, yOffset: lock ? 2 : -4, xOffset: lock ? 0 : Math.sin(i) * 3 };
    }

    // --- Headbang ---
    case 'headbang': {
      // Violent nods with hair-whip arc, tiny stomp bounce
      const b = Math.sin(t * 0.021);
      const whip = Math.sign(b) * Math.pow(Math.abs(b), 0.7);
      return { ...DEFAULT_TRANSFORM, yOffset: Math.abs(whip) * 8, rotation: whip * 0.3, scaleY: 1 - Math.abs(whip) * 0.1, scaleX: 1 + Math.abs(whip) * 0.05, xOffset: Math.sin(t * 0.0105) * 2 };
    }

    // --- Moonwalk ---
    case 'moonwalk': {
      // Glide backwards with reversed leg cycle and iconic back lean
      const glide = ((t * 0.055) % 90) - 45;
      return { ...DEFAULT_TRANSFORM, direction: 'right', walkPhase: 8 - ((t * 0.018) % 8), isWalking: true, xOffset: -glide, rotation: -0.14, yOffset: -Math.abs(Math.sin(t * 0.018)) * 2, scaleY: 1.03 };
    }

    default:
      return DEFAULT_TRANSFORM;
  }
}
